import {HttpStatusCode} from '@angular/common/http';
import {Component} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {ReferenceBookApiService, ReferenceBookResponse, ReferenceBookType} from '@npck/af-frontend-api';
import {RouterService, rxSpinner} from '@npck/libs/commons';
import {SnackbarService} from '@npck/libs/ui-kit';
import {map, Observable} from 'rxjs';
import {DictionaryHolderService} from '../../../../domain/dictionary-holder.service';
import {DICTIONARY_ITEM_ID, DICTIONARY_NAME} from '../../../../domain/query-params.keys';
import {Path} from '../../../../path';
import {AdminPanelTab} from '../../admin.component';

@Component({
    selector: 'af-dictionary-create-edit',
    templateUrl: './dictionary-create-edit.component.html',
    styleUrl: './dictionary-create-edit.component.scss',
    standalone: false
})
export class DictionaryCreateEditComponent {
    form: FormGroup<DictionaryForm>;
    dictionaryName: string;
    dictionaryItemId: string;
    tabPathToTab: Record<string, AdminPanelTab> = {[Path.DICTIONARIES]: 'tabDictionaries'};
    controlNames: (keyof DictionaryForm)[] = ['name_kz', 'name_ru', 'name_en', 'code', 'code1', 'code2', 'type'];
    Validators = Validators;

    constructor(
        private formBuilder: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private dictionaryHolderService: DictionaryHolderService,
        private routerService: RouterService,
        private snackbarService: SnackbarService,
        private referenceBookApiService: ReferenceBookApiService,
    ) {
        this.dictionaryName = this.activatedRoute.snapshot.paramMap.get(DICTIONARY_NAME);
        this.dictionaryItemId = this.activatedRoute.snapshot.paramMap.get(DICTIONARY_ITEM_ID);

        if (this.dictionaryItemId) {
            this.dictionaryHolderService.getReference(this.dictionaryName)
                .pipe(
                    rxSpinner('initial'),
                    map(result => result.find(item => item.id === this.dictionaryItemId)),
                )
                .subscribe(res => this.buildForm(res));
        } else {
            this.buildForm();
        }
    }

    filterPath = (path: string): boolean => {
        return path === this.dictionaryName;
    };

    submit(): void {
        let request$: Observable<ReferenceBookResponse>;
        const request = <ReferenceBookResponse>{
            ...this.form.getRawValue(),
            type: this.form.getRawValue().type.toUpperCase() as ReferenceBookType,
        };
        if (this.dictionaryItemId) {
            request$ = this.referenceBookApiService.updateReferenceBookValue(this.dictionaryItemId, request);
        } else {
            request$ = this.referenceBookApiService.createReferenceBookValue(request);
        }
        request$.subscribe({
            next: result => {
                this.dictionaryHolderService.addOrUpdateExistingCachedValue(result);
                this.snackbarService.showSnackBar(
                    this.dictionaryItemId
                        ? 'i18n_af_snackbarMessageDictionaryItemUpdated'
                        : 'i18n_af_snackbarMessageDictionaryItemCreated',
                    'success',
                    false,
                    {id: this.dictionaryItemId});
                this.navigateBack();
            },
            error: err => {
                if (err.status === HttpStatusCode.Conflict) {
                    this.snackbarService.showSnackBar('i18n_af_snackbarMessageDictionaryCreateUpdateItemConflict', 'error', false);
                } else {
                    throw err;
                }
            },
        });
    }

    navigateBack(): void {
        this.routerService.navigateToTab<AdminPanelTab>(Path.ADMIN_PANEL, 'tabDictionaries');
    }

    private buildForm(referenceBook?: ReferenceBookResponse): void {
        this.form = this.formBuilder.group({
            type: new FormControl({value: referenceBook?.type, disabled: this.dictionaryName !== 'reference'}, [Validators.required]),
            code: new FormControl(referenceBook?.code, [Validators.required]),
            code1: new FormControl(referenceBook?.code1),
            code2: new FormControl(referenceBook?.code2),
            name_kz: new FormControl(referenceBook?.name_kz, [Validators.required]),
            name_ru: new FormControl(referenceBook?.name_ru, [Validators.required]),
            name_en: new FormControl(referenceBook?.name_en),
        });
    }
}

interface DictionaryForm {
    type: FormControl<ReferenceBookType>;
    code: FormControl<string>;
    code1: FormControl<string>;
    code2: FormControl<string>;
    name_kz: FormControl<string>;
    name_ru: FormControl<string>;
    name_en: FormControl<string>;
}
